/**
 * 图片浏览器本地缓存工具类
 * 使用 IndexedDB 存储图片数据，减少服务器请求
 */

const DB_NAME = 'blog_image_cache'
const DB_VERSION = 1
const STORE_NAME = 'images'

let db = null
let initPromise = null  // 保存初始化 Promise，避免重复打开

/**
 * 初始化 IndexedDB
 */
export function initImageCache() {
    // 如果已经初始化完成，直接返回
    if (db) {
        return Promise.resolve(db)
    }
    
    // 如果正在初始化，返回同一个 Promise
    if (initPromise) {
        return initPromise
    }

    initPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION)

        request.onerror = (event) => {
            console.error('IndexedDB 打开失败:', event.target.error)
            initPromise = null  // 重置，允许重试
            reject(event.target.error)
        }

        request.onsuccess = (event) => {
            db = event.target.result
            initPromise = null
            resolve(db)
        }

        request.onupgradeneeded = (event) => {
            const database = event.target.result

            // 创建对象存储空间
            if (!database.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = database.createObjectStore(STORE_NAME, { keyPath: 'url' })
                objectStore.createIndex('lastAccess', 'lastAccess', { unique: false })
                objectStore.createIndex('size', 'size', { unique: false })
            }
        }
    })
    
    return initPromise
}

/**
 * 从缓存中获取图片
 * @param {string} imageUrl - 图片 URL
 * @returns {Promise<string|null>} - 返回 Base64 格式的图片数据，如果不存在则返回 null
 */
export function getImageFromCache(imageUrl) {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve(null)
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readonly')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.get(imageUrl)

        request.onsuccess = (event) => {
            const result = event.target.result
            if (result) {
                // 更新最后访问时间
                updateImageAccessTime(imageUrl)
                resolve(result.data)
            } else {
                resolve(null)
            }
        }

        request.onerror = (event) => {
            console.error('从缓存读取图片失败:', event.target.error)
            reject(event.target.error)
        }
    })
}

/**
 * 将图片保存到缓存
 * @param {string} imageUrl - 图片 URL
 * @param {string} imageData - Base64 格式的图片数据
 */
export function saveImageToCache(imageUrl, imageData) {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve(false)
            return
        }

        try {
            const transaction = db.transaction([STORE_NAME], 'readwrite')
            const objectStore = transaction.objectStore(STORE_NAME)
            
            const data = {
                url: imageUrl,
                data: imageData,
                size: imageData.length,
                lastAccess: Date.now(),
                createTime: Date.now()
            }

            const request = objectStore.put(data)

            request.onsuccess = () => {
                resolve(true)
            }

            request.onerror = (event) => {
                console.error('保存图片到缓存失败:', event.target.error)
                reject(event.target.error)
            }
        } catch (error) {
            console.error('保存图片到缓存异常:', error)
            resolve(false)
        }
    })
}

/**
 * 更新图片的最后访问时间
 * @param {string} imageUrl - 图片 URL
 */
function updateImageAccessTime(imageUrl) {
    if (!db) return

    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const objectStore = transaction.objectStore(STORE_NAME)
    
    const getRequest = objectStore.get(imageUrl)
    
    getRequest.onsuccess = (event) => {
        const data = event.target.result
        if (data) {
            data.lastAccess = Date.now()
            objectStore.put(data)
        }
    }
}

/**
 * 清理过期的缓存图片（超过 7 天未访问）
 */
export function clearExpiredCache() {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve(0)
            return
        }

        const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000)
        let count = 0

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const index = objectStore.index('lastAccess')

        const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo))

        request.onsuccess = (event) => {
            const cursor = event.target.result
            if (cursor) {
                cursor.delete()
                count++
                cursor.continue()
            } else {
                console.log(`清理了 ${count} 张过期图片`)
                resolve(count)
            }
        }

        request.onerror = (event) => {
            console.error('清理过期缓存失败:', event.target.error)
            reject(event.target.error)
        }
    })
}

/**
 * 获取缓存统计信息
 * @returns {Promise<{count: number, size: number}>}
 */
export function getCacheStats() {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve({ count: 0, size: 0 })
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readonly')
        const objectStore = transaction.objectStore(STORE_NAME)
        
        let count = 0
        let size = 0

        const request = objectStore.openCursor()

        request.onsuccess = (event) => {
            const cursor = event.target.result
            if (cursor) {
                count++
                size += cursor.value.size || 0
                cursor.continue()
            } else {
                resolve({ count, size })
            }
        }

        request.onerror = (event) => {
            console.error('获取缓存统计失败:', event.target.error)
            reject(event.target.error)
        }
    })
}

/**
 * 清空所有缓存
 */
export function clearAllCache() {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve()
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.clear()

        request.onsuccess = () => {
            console.log('已清空所有图片缓存')
            resolve()
        }

        request.onerror = (event) => {
            console.error('清空缓存失败:', event.target.error)
            reject(event.target.error)
        }
    })
}

// 自动初始化
initImageCache().catch(err => console.error('图片缓存初始化失败:', err))

// 每天检查一次过期缓存
setInterval(() => {
    clearExpiredCache().catch(err => console.error('定期清理缓存失败:', err))
}, 24 * 60 * 60 * 1000)
